import { useState, useCallback } from 'react';
import { createContainer } from 'unstated-next';
import { useDataProvider, useGetOne } from 'react-admin';
import {
  BuilderDto,
  BuilderCreateDto,
  BuilderUpdateDto,
  SpBuilderNameInterface,
} from '@vatos-pas/common';

export interface BuilderFormValues {
  name: string;
  code: string;
}

/**
 * Adapter between the data layer and builder components — components stay
 * data-layer agnostic so the underlying client (react-admin, react-query,
 * Apollo, fetch) can be swapped without touching them.
 *
 * `useGetOne` provides SWR semantics via the react-admin Redux cache: cached
 * data returns immediately, refetch happens in the background, and the loading
 * spinner only shows on first load when no cached data exists.
 */
function useBuilder() {
  const dataProvider = useDataProvider();

  const [builderId, setBuilderId] = useState<string>('');
  const {
    data: selectedBuilder,
    loading: builderLoading,
    loaded: builderLoaded,
    error: builderError,
  } = useGetOne<BuilderDto>('builder', builderId, { enabled: !!builderId });

  const isLoading = !!builderId && !selectedBuilder && builderLoading;

  const [formLoading, setFormLoading] = useState(false);

  const [spBuilders, setSpBuilders] = useState<SpBuilderNameInterface[]>([]);
  const [spBuildersLoading, setSpBuildersLoading] = useState(false);

  const loadBuilderById = useCallback((id: string): void => {
    setBuilderId(id);
  }, []);

  const createBuilder = useCallback(
    async (data: BuilderCreateDto): Promise<BuilderDto> => {
      const result = await dataProvider.create<BuilderDto>('builder', { data });
      setBuilderId(result.data.id);
      return result.data;
    },
    [dataProvider],
  );

  const updateBuilder = useCallback(
    async (id: string, data: BuilderUpdateDto): Promise<BuilderDto> => {
      const result = await dataProvider.update<BuilderDto>('builder', {
        id,
        data: { ...selectedBuilder, ...data },
        previousData: selectedBuilder || { id },
      });
      return result.data;
    },
    [dataProvider, selectedBuilder],
  );

  const loadSpBuilders = useCallback(
    async (builderId: string): Promise<SpBuilderNameInterface[]> => {
      setSpBuildersLoading(true);
      try {
        const result = await dataProvider.getList<SpBuilderNameInterface>(
          'sp-builder-name',
          {
            filter: { builderId },
            pagination: { page: 1, perPage: 100 },
            sort: { field: 'spName', order: 'ASC' },
          },
        );
        setSpBuilders(result.data);
        return result.data;
      } finally {
        setSpBuildersLoading(false);
      }
    },
    [dataProvider],
  );

  const createSpBuilder = useCallback(
    async (
      builderId: string,
      spName: string,
      active: boolean,
    ): Promise<SpBuilderNameInterface> => {
      const spNameTrimmed = spName.trim();
      if (spNameTrimmed.length === 0) {
        throw new Error('Builder name cannot be empty');
      }

      const result = await dataProvider.create<SpBuilderNameInterface>(
        'sp-builder-name',
        {
          data: {
            builderId,
            spName: spNameTrimmed,
            active,
          },
        },
      );
      setSpBuilders(prev => [...prev, result.data]);
      return result.data;
    },
    [dataProvider],
  );

  const updateSpBuilderActive = useCallback(
    async (id: string, active: boolean): Promise<void> => {
      const currentRow = spBuilders.find(sp => sp.id === id);
      await dataProvider.update('sp-builder-name', {
        id,
        data: { active },
        previousData: currentRow || { id },
      });
      setSpBuilders(prev =>
        prev.map(sp => (sp.id === id ? { ...sp, active } : sp)),
      );
    },
    [dataProvider, spBuilders],
  );

  return {
    selectedBuilder,
    isLoading,
    builderLoaded,
    builderError,
    formLoading,
    setFormLoading,
    loadBuilderById,
    createBuilder,
    updateBuilder,
    spBuilders,
    spBuildersLoading,
    loadSpBuilders,
    createSpBuilder,
    updateSpBuilderActive,
  };
}

export const BuilderContainer = createContainer(useBuilder);
